import { useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { Phone, ChevronRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/site/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/site/Footer";
import FloatingCTAs from "@/components/site/FloatingCTAs";
import logo from "@/assets/logo.png";
import InsuranceCTABlock from "@/components/site/InsuranceCTABlock";

const PHONE = "856-200-3127";
const TEL = "tel:8562003127";
const CANONICAL =
  "https://cherryhillrecoverycenter.com/evening-iop-new-jersey";

const BURGUNDY = "#8B1A1A";
const CRIMSON = "#6B1414";
const CREAM = "#FAF4F0";
const BODY = "#2C2C2C";
const RULE = "#F0E0E0";

const faqs = [
  {
    q: "What time does evening IOP start?",
    a: "Cherry Hill Recovery Center's evening IOP sessions typically begin between 5pm and 6pm — allowing patients to complete a full workday before attending. Call 856-200-3127 to confirm current scheduling and find the time that works for your situation.",
  },
  {
    q: "How many days per week is evening IOP?",
    a: "IOP at Cherry Hill Recovery Center runs three to four days per week with each session approximately three hours. The specific schedule is confirmed during your clinical assessment based on your clinical needs and availability.",
  },
  {
    q: "Is evening IOP as effective as daytime IOP?",
    a: "Yes. The clinical content, therapeutic approach, and quality of care in evening IOP is identical to daytime IOP. Research on IOP outcomes does not show meaningful differences in effectiveness based on session timing. The scheduling flexibility is a logistical accommodation — not a clinical compromise.",
  },
  {
    q: "Can I do evening IOP while working full-time?",
    a: "Yes — this is exactly what evening IOP is designed for. Your work schedule is completely unaffected. Many patients at Cherry Hill Recovery Center successfully complete evening IOP while maintaining full-time employment. Our clinical team works with you on time management and daily structure throughout the program.",
  },
  {
    q: "Will my employer find out I am in treatment?",
    a: "Not from us. Your treatment is protected under HIPAA and 42 CFR Part 2 — the federal law specifically governing addiction treatment confidentiality. Evening IOP also protects your privacy practically — your work schedule is completely unchanged, so there are no unexplained absences or disruptions that would require explanation.",
  },
  {
    q: "Does insurance cover evening IOP in New Jersey?",
    a: "Yes. Most major insurance plans cover IOP under the Mental Health Parity Act. Coverage applies to evening sessions the same as daytime. Our admissions team verifies your specific coverage before treatment begins at no cost to you. Call 856-200-3127 to start.",
  },
  {
    q: "Is virtual evening IOP available in New Jersey?",
    a: "Yes. Cherry Hill Recovery Center offers Virtual IOP — the same structured evening programming delivered securely via telehealth. Available to patients anywhere in New Jersey. Particularly useful for patients with long commutes, family responsibilities in the evening, or who live outside South Jersey.",
  },
  {
    q: "How is evening IOP different from PHP?",
    a: "PHP — Partial Hospitalization Program — runs five to six days per week, typically five to eight hours per day, and is the highest level of outpatient care. IOP runs three to four days per week for approximately three hours per session and is designed for patients who need meaningful clinical support while maintaining daily responsibilities. Many patients complete PHP and then step down to IOP as a natural progression in their recovery.",
  },
  {
    q: "How long does evening IOP last?",
    a: "Most patients attend evening IOP for eight to twelve weeks, though length of stay is clinical rather than fixed — some step down sooner, others continue longer. Your treatment plan is reviewed regularly and adjusted based on progress, stability, and any co-occurring conditions being treated.",
  },
  {
    q: "Who is evening IOP appropriate for?",
    a: "Evening IOP fits patients who are medically stable, do not need detox or 24-hour supervision, and cannot attend daytime treatment — working professionals, parents and caregivers, college students, shift workers, and patients stepping down from PHP. A free clinical assessment confirms whether this is the right level of care before you commit to anything.",
  },
  {
    q: "Can I transition from PHP to evening IOP?",
    a: "Yes — this is one of the most common paths at Cherry Hill Recovery Center. Patients finishing PHP often return to work and step down into evening IOP with the same clinical team, so there is no handoff to a new provider and no gap in care.",
  },
  {
    q: "Is evening IOP available in Cherry Hill and South Jersey?",
    a: "Yes. Evening IOP is delivered at our Cherry Hill location at 1930 Marlton Pike East, off Route 70 and minutes from I-295 — convenient for patients across Camden, Burlington, and Gloucester counties. Virtual evening IOP is available statewide for patients elsewhere in New Jersey.",
  },
];


const relatedPosts = [
  {
    title: "How IOP Programs Support Addiction Recovery Without Full Hospitalization",
    href: "/how-iop-programs-support-addiction-recovery-nj",
  },
  {
    title: "IOP with Housing Options in New Jersey — Combining Structure and Stability",
    href: "/iop-with-housing-options-nj",
  },
  {
    title: "Intensive Outpatient Program (IOP) in Cherry Hill, NJ",
    href: "/iop-cherry-hill-nj",
  },
];

const toc = [
  { id: "what-is", label: "What Is Evening IOP?" },
  { id: "right-for", label: "Who Is It Right For?" },
  { id: "includes", label: "What It Includes" },
  { id: "vs-daytime", label: "Evening vs Daytime IOP" },
  { id: "working", label: "Working Full-Time and IOP" },
  { id: "cherry-hill", label: "Evening IOP in Cherry Hill" },

  { id: "virtual", label: "Virtual Evening IOP" },
  { id: "not-right", label: "When It's Not the Right Starting Point" },
  { id: "get-started", label: "How to Get Started" },
  { id: "faq", label: "Frequently Asked Questions" },
];

const TODAY_ISO = new Date().toISOString().slice(0, 10);
const TODAY_LABEL = new Date().toLocaleDateString("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

const linkCls = "underline underline-offset-2 hover:opacity-80";
const linkStyle = { color: BURGUNDY };

const BlogEveningIOPNJ = () => {
  
  const h2Style = { color: BURGUNDY, fontSize: 28 };
  const sectionRule = (
    <hr className="my-10" style={{ borderColor: RULE, borderTopWidth: 1 }} />
  );

  const Bullet = ({ lead, children }: { lead: string; children: React.ReactNode }) => (
    <li className="flex gap-3">
      <span
        aria-hidden
        className="mt-2 flex-shrink-0 rounded-full"
        style={{ width: 8, height: 8, background: BURGUNDY }}
      />
      <span>
        <strong style={{ color: BURGUNDY }}>{lead}</strong> {children}
      </span>
    </li>
  );

  return (
    <div style={{ background: "#FFFFFF", color: BODY }} className="min-h-screen flex flex-col">
      <SEOHead title={"Evening IOP in New Jersey — Addiction Treatment After Work"} description={"Evening IOP in Cherry Hill, NJ — structured addiction treatment after work or school. Same clinical quality, flexible evening schedule. Call 856-200-3127."} canonical={CANONICAL} schemaJson={[...([
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline:
          "Evening IOP in New Jersey — Addiction Treatment That Works Around Your Life",
        datePublished: "2024-02-18",
        dateModified: TODAY_ISO,
        author: {
          "@type": "Organization",
          name: "Cherry Hill Recovery Center Clinical Team",
        },
        publisher: {
          "@type": "MedicalOrganization",
          name: "Cherry Hill Recovery Center",
          logo: {
            "@type": "ImageObject",
            url: "https://cherryhillrecoverycenter.com/logo.png",
          },
        },
        description:
          "Evening IOP in New Jersey — structured addiction treatment scheduled after work or school. Same clinical quality as daytime IOP. Cherry Hill Recovery Center, Cherry Hill, NJ.",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": CANONICAL,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://cherryhillrecoverycenter.com/" },
          { "@type": "ListItem", position: 2, name: "Blog", item: "https://cherryhillrecoverycenter.com/blog" },
          {
            "@type": "ListItem",
            position: 3,
            name: "Evening IOP in New Jersey",
            item: CANONICAL,
          },
        ],
      },
    ])]} />
      <Header />

      <main className="flex-1">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="border-b" style={{ borderColor: RULE, background: CREAM }}>
          <div className="container py-4 text-sm" style={{ color: "#888888" }}>
            <ol className="flex flex-wrap items-center gap-2">
              <li><Link to="/" className="hover:underline" style={{ color: BURGUNDY }}>Home</Link></li>
              <ChevronRight className="h-3.5 w-3.5" />
              <li><Link to="/" className="hover:underline" style={{ color: BURGUNDY }}>Blog</Link></li>
              <ChevronRight className="h-3.5 w-3.5" />
              <li aria-current="page" style={{ color: BODY }}>Evening IOP in New Jersey</li>
            </ol>
          </div>
        </nav>

        <div className="container py-10 lg:py-14">
          <div className="grid lg:grid-cols-[1fr_320px] gap-10">
            <article>
              {/* Header */}
              <header className="mb-6">
                <span
                  className="inline-block font-semibold mb-5"
                  style={{
                    background: CREAM,
                    color: BURGUNDY,
                    borderRadius: 50,
                    padding: "4px 14px",
                    fontSize: 12,
                  }}
                >
                  IOP & PHP
                </span>
                <h1
                  className="font-display font-bold leading-tight mb-5"
                  style={{ color: BURGUNDY, fontSize: "clamp(28px, 4vw, 40px)" }}
                >
                  Evening IOP in New Jersey — Addiction Treatment That Works Around Your Life
                </h1>
                <div className="text-sm space-y-1.5">
                  <p style={{ color: "#888888" }}>
                    Published: February 18, 2024 · Updated: {TODAY_LABEL} · 7 min read
                  </p>
                  <p style={{ color: BODY }}>
                    Author: Cherry Hill Recovery Center Clinical Team
                  </p>
                  <p className="italic" style={{ color: "#888888", fontSize: 13 }}>
                    Medically reviewed by{" "}
                    <Link to="/about" className={linkCls} style={linkStyle}>
                      Dr. Jeffrey Simon, MD
                    </Link>
                    , Medical Director — Cherry Hill Recovery Center. Our licensed clinicians and master's-level therapists deliver CBT, DBT, motivational interviewing, and medication-assisted treatment in a Joint Commission-accredited, New Jersey-licensed program.
                  </p>

                </div>
                <hr className="mt-5" style={{ borderColor: RULE, borderTopWidth: 1 }} />
              </header>

              {/* Body */}
              <div style={{ color: BODY, fontSize: 17, lineHeight: 1.75 }}>
                <p className="mb-5">
                  One of the most common reasons people delay getting help for addiction is the belief that treatment requires putting their entire life on hold. For working adults, parents, and students — this belief is one of the most significant barriers between where they are and where they want to be. Evening IOP exists specifically to remove that barrier. It delivers the same clinically rigorous{" "}
                  <Link to="/iop-cherry-hill-nj" className={linkCls} style={linkStyle}>
                    Intensive Outpatient
                  </Link>{" "}
                  programming as a standard daytime schedule — individual therapy, group counseling, psychiatric evaluation, and evidence-based skill building — scheduled after work or school so that treatment and daily life can happen at the same time.
                </p>

                {sectionRule}

                <h2 id="what-is" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  What Is Evening IOP?
                </h2>
                <p className="mb-5">
                  IOP — Intensive Outpatient Program — is a structured addiction treatment format that provides clinical care three to four days per week, approximately three hours per session. It sits between{" "}
                  <Link to="/php-cherry-hill-nj" className={linkCls} style={linkStyle}>
                    PHP — Partial Hospitalization Program
                  </Link>
                  , which runs five to six days per week with full-day sessions — and standard once-weekly outpatient therapy. IOP provides enough structure and clinical contact to support early recovery while allowing patients to return home each day and maintain their normal responsibilities.
                </p>
                <p>
                  Evening IOP is IOP scheduled in the late afternoon or evening — typically starting between 5pm and 6pm — so that patients can work a full day, attend classes, or handle daytime obligations before coming to treatment. The clinical content is identical to daytime IOP. The staff are the same licensed clinicians. The therapeutic quality is the same. The only difference is the time on the clock.
                </p>

                {sectionRule}

                <h2 id="right-for" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  Who Is Evening IOP Right For?
                </h2>
                <p className="mb-5">
                  Evening IOP tends to be the right level of care for people who need structured clinical support but cannot commit to a full-day daytime program without significant disruption to their work, income, or family life. It is a particularly strong fit for:
                </p>
                <ul className="space-y-4 mb-5">
                  <Bullet lead="Working adults who cannot miss work:">
                    Whether due to financial necessity, professional standing, or personal preference — evening IOP allows full-time employment to continue completely uninterrupted. Your employer does not need to know you are in treatment.
                  </Bullet>
                  <Bullet lead="College and graduate students:">
                    Coursework, labs, and class schedules stay fully intact. Treatment happens after academic hours.
                  </Bullet>
                  <Bullet lead="Parents with daytime childcare responsibilities:">
                    School pickup, after-school activities, and daytime parenting are completely unaffected by an evening IOP schedule.
                  </Bullet>
                  <Bullet lead="Professionals in high-visibility roles:">
                    For people where taking extended time off would carry real professional risk — lawyers, healthcare workers, executives — evening IOP removes the career exposure that can make daytime programming feel impossible.
                  </Bullet>
                  <Bullet lead="Shift and rotating-schedule workers:">
                    Nurses, first responders, tradespeople, and hospitality staff whose hours change week to week — attendance nights are built around your rotation, with virtual sessions covering the weeks that do not line up.
                  </Bullet>
                  <Bullet lead="Patients stepping down from PHP:">
                    Patients completing a{" "}
                    <Link to="/php-cherry-hill-nj" className={linkCls} style={linkStyle}>
                      PHP program
                    </Link>{" "}
                    who are clinically ready to reduce intensity but not ready to step down to weekly therapy benefit from the continued structure of IOP — often transitioning to an evening schedule as they return to work.
                  </Bullet>
                  <Bullet lead="Anyone who simply cannot attend daytime treatment:">
                    Caregivers for aging parents, self-employed people who lose income for every hour away, and patients without daytime transportation all use evening outpatient rehab in NJ as the only realistic path into care.
                  </Bullet>
                </ul>


                {sectionRule}

                <h2 id="includes" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  What Evening IOP at Cherry Hill Recovery Center Includes
                </h2>
                <p className="mb-5">
                  Evening IOP programming at Cherry Hill Recovery Center includes the full range of clinical services delivered in our daytime programs — scheduled to accommodate work and daily life.
                </p>
                <ul className="space-y-4 mb-5">
                  <Bullet lead="Group therapy:">
                    Evidence-based group sessions covering relapse prevention, coping skills, trigger management, and recovery strategies — the core clinical work of IOP.
                  </Bullet>
                  <Bullet lead="Individual therapy:">
                    One-on-one sessions with a licensed clinician scheduled on a rotating basis throughout the week alongside group sessions.
                  </Bullet>
                  <Bullet lead="Psychiatric evaluation and medication management:">
                    Comprehensive{" "}
                    <Link to="/psychiatric-evaluation-nj" className={linkCls} style={linkStyle}>
                      psychiatric evaluation
                    </Link>{" "}
                    for co-occurring conditions including depression, anxiety, ADHD, PTSD, and bipolar disorder — integrated into the program, not referred out.
                  </Bullet>
                  <Bullet lead="MAT coordination:">
                    For patients receiving{" "}
                    <Link to="/medication-assisted-treatment-nj" className={linkCls} style={linkStyle}>
                      Medication Assisted Treatment
                    </Link>{" "}
                    with Suboxone, Sublocade, or{" "}
                    <Link to="/vivitrol-shot-duration-effectiveness-nj" className={linkCls} style={linkStyle}>
                      Vivitrol
                    </Link>{" "}
                    — medication management is coordinated within the program when clinically appropriate and agreed upon.
                  </Bullet>
                  <Bullet lead="Family involvement:">
                    Family education and therapy sessions available when clinically appropriate and agreed upon by the patient.
                  </Bullet>
                </ul>

                {sectionRule}

                <h2 id="vs-daytime" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  Evening IOP vs Daytime IOP — Is There a Clinical Difference?
                </h2>
                <p className="mb-5">
                  The clinical content and quality of evening IOP is identical to daytime IOP. Same evidence-based therapies. Same licensed clinical staff. Same psychiatric integration. The decision between evening and daytime IOP is driven entirely by the patient's schedule and life circumstances — not by clinical severity or quality of care.
                </p>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-left" style={{ fontSize: 14, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: CREAM }}>
                        <th className="p-3" style={{ color: BURGUNDY, border: `1px solid ${RULE}` }}></th>
                        <th className="p-3" style={{ color: BURGUNDY, border: `1px solid ${RULE}` }}>Evening IOP</th>
                        <th className="p-3" style={{ color: BURGUNDY, border: `1px solid ${RULE}` }}>Daytime IOP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Schedule", "Starts 5–6pm, 3–4 evenings per week, about 3 hours per session", "Morning or early-afternoon groups, 3–4 days per week, about 3 hours per session"],
                        ["Flexibility", "Built for fixed daytime obligations; virtual nights available when work travels", "Best when your days are open or you are on leave from work"],
                        ["Work & school compatibility", "No PTO, no reduced hours, no missed classes", "Usually requires a modified work schedule or time off"],
                        ["Who it suits", "Working professionals, parents, students, shift workers, PHP step-downs", "Patients not currently working, on FMLA, or needing early-day structure"],
                        ["Treatment structure", "Identical curriculum — group, individual therapy, psychiatric care, MAT coordination, family work", "Identical curriculum — same clinicians and evidence-based model"],
                      ].map(([label, evening, daytime]) => (
                        <tr key={label}>
                          <td className="p-3 font-semibold" style={{ border: `1px solid ${RULE}`, color: BURGUNDY }}>{label}</td>
                          <td className="p-3" style={{ border: `1px solid ${RULE}` }}>{evening}</td>
                          <td className="p-3" style={{ border: `1px solid ${RULE}` }}>{daytime}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  There is one nuance worth noting: the after-work timing means patients arrive having already navigated a full day — with its stressors, triggers, and demands — before the session begins. For many patients this is genuinely valuable. The therapeutic material covered in group is immediately relevant to what they experienced that day. Real-time processing of real-time challenges is one of the most effective forms of clinical work. Our clinical team is experienced managing the fatigue dimension of evening programming and works with patients on structuring their day to arrive ready. If your days are already open, the{" "}
                  <Link to="/iop-cherry-hill-nj" className={linkCls} style={linkStyle}>
                    standard IOP schedule in Cherry Hill
                  </Link>{" "}
                  may be the better fit.
                </p>


                {sectionRule}

                <h2 id="working" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  Can You Really Work Full-Time and Do IOP at the Same Time?
                </h2>
                <p className="mb-5">
                  Yes — and evening IOP makes it genuinely achievable for most working adults. The people who do best in evening IOP share one approach: they treat their treatment schedule with the same seriousness they treat their work schedule. They block the time, protect it, and do not allow work to consistently encroach on it.
                </p>
                <p className="mb-5">
                  That sounds obvious — but for high-achieving professionals and students who have spent years prioritizing performance over everything else, learning to protect treatment time is itself meaningful clinical work. Our therapists work with every evening IOP patient on time management, boundary setting, and building a daily structure that holds both work and treatment without either consuming the other.
                </p>
                <p className="mb-4">Here is what balancing after-work addiction treatment in NJ actually looks like week to week:</p>
                <ul className="space-y-4 mb-6">
                  <Bullet lead="Full-time employment:">
                    You keep your hours, your income, and your benefits. Sessions begin after the standard workday, so there is no PTO request, no reduced schedule, and no gap on your timesheet to explain.
                  </Bullet>
                  <Bullet lead="School and coursework:">
                    Most college and graduate schedules end before group starts. When a required evening class conflicts, we look at which nights you attend or move you to virtual sessions rather than pausing treatment.
                  </Bullet>
                  <Bullet lead="Family responsibilities:">
                    School pickup, daytime caregiving, and morning routines stay yours. We help patients plan evening childcare coverage for their treatment nights and involve partners or parents in family sessions when you want them there.
                  </Bullet>
                  <Bullet lead="Shift and rotating schedules:">
                    Nurses, trades, warehouse, and hospitality workers rarely fit a 9-to-5 program. We build the attendance days around your rotation and use virtual sessions on weeks when your shift changes.
                  </Bullet>
                  <Bullet lead="Recovery outside of session:">
                    Flexible IOP in NJ only works if the rest of the week is structured too. Your plan includes sleep, meals, mutual-aid or alumni support, and a written relapse-prevention plan for the hours you are not in group.
                  </Bullet>
                </ul>


                {/* Privacy callout */}
                <aside
                  className="my-6"
                  style={{
                    background: CREAM,
                    borderLeft: `4px solid ${BURGUNDY}`,
                    padding: "20px 24px",
                    borderRadius: 8,
                  }}
                >
                  <p className="font-bold mb-2" style={{ color: BURGUNDY, fontSize: 15 }}>
                    What About Privacy at Work?
                  </p>
                  <p style={{ color: BODY, fontSize: 14, lineHeight: 1.7 }}>
                    Evening IOP protects your workplace privacy more effectively than any daytime program. Your work schedule is completely unchanged — no unexplained absences, no schedule disruptions, nothing that requires explanation to an employer. Your treatment is protected under HIPAA and 42 CFR Part 2 — the federal law specifically governing addiction treatment confidentiality. You are under no obligation to disclose that you are in treatment to anyone.
                  </p>
                </aside>

                {sectionRule}

                <h2 id="cherry-hill" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  Evening IOP in Cherry Hill for South Jersey Residents
                </h2>
                <p className="mb-5">
                  Our evening groups run out of 1930 Marlton Pike East in Cherry Hill, right on Route 70 and minutes from I-295 and Route 38, with free on-site parking. That location is the practical reason evening addiction treatment in NJ works for so many patients here: most people driving from Camden, Voorhees, Marlton, Moorestown, Haddonfield, Mount Laurel, or Collingswood are in the building within 20 minutes of leaving work, and the Ben Franklin and Walt Whitman bridges keep us reachable for commuters coming back from Philadelphia.
                </p>
                <p className="mb-5">
                  Sessions start between 5pm and 6pm and run about three hours, so you finish your shift, come in, and sleep in your own bed. Nothing about your housing, your childcare arrangement, or your commute has to change. For families in Camden, Burlington, and Gloucester counties, that is often the difference between starting treatment now and putting it off another six months.
                </p>
                <p>
                  Evening IOP in Cherry Hill, NJ sits alongside our{" "}
                  <Link to="/iop-cherry-hill-nj" className={linkCls} style={linkStyle}>
                    daytime IOP program in Cherry Hill
                  </Link>
                  ,{" "}
                  <Link to="/php-cherry-hill-nj" className={linkCls} style={linkStyle}>
                    PHP in Cherry Hill, NJ
                  </Link>
                  , and{" "}
                  <Link to="/medication-assisted-treatment-nj" className={linkCls} style={linkStyle}>
                    medication-assisted treatment with Suboxone or Vivitrol
                  </Link>{" "}
                  — same team, same building, so a change in level of care never means starting over with a new provider. See every community we serve on our{" "}
                  <Link to="/service-areas" className={linkCls} style={linkStyle}>
                    South Jersey service areas page
                  </Link>
                  .
                </p>

                {sectionRule}


                <h2 id="virtual" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  Virtual Evening IOP — Treatment From Home After Work
                </h2>
                <p className="mb-5">
                  Cherry Hill Recovery Center also offers{" "}
                  <Link to="/virtual-iop-new-jersey" className={linkCls} style={linkStyle}>
                    Virtual IOP
                  </Link>{" "}
                  — the same evening IOP programming delivered securely via telehealth for patients anywhere in New Jersey. Virtual evening IOP is particularly well-suited for patients with long commutes after work, those with children at home in the evenings, and patients who live outside the immediate Cherry Hill area.
                </p>
                <p>
                  The clinical quality of virtual IOP is identical to in-person. Group therapy, individual sessions, and psychiatric oversight all happen via secure video — the only thing missing is the physical commute.
                </p>

                {sectionRule}

                <h2 id="not-right" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  When Evening IOP Is Not the Right Starting Point
                </h2>
                <p className="mb-5">
                  Evening IOP is not appropriate for every patient. Clinical assessment determines the right level of care — and for some patients, a higher level of structure is what the clinical picture actually requires. Evening IOP is generally not the right starting point for:
                </p>
                <ul className="space-y-4 mb-5">
                  <Bullet lead="Patients who need detox first:">
                    Those completing alcohol, opioid, or benzodiazepine detox typically step down into PHP before transitioning to IOP.{" "}
                    <Link to="/detox-referral-coordination-nj" className={linkCls} style={linkStyle}>
                      Detox referral
                    </Link>{" "}
                    and coordination is available through Cherry Hill Recovery Center before PHP or IOP begins.
                  </Bullet>
                  <Bullet lead="Patients with severe co-occurring psychiatric conditions:">
                    Those requiring intensive daily clinical oversight for significant mental health conditions may need PHP before IOP.
                  </Bullet>
                  <Bullet lead="Highly unstable environments:">
                    Patients whose home or work environment is an active and uncontrolled relapse trigger may need the more intensive daily structure of PHP first.
                  </Bullet>
                  <Bullet lead="Multiple prior IOP attempts without sustained recovery:">
                    A pattern of repeated IOP attempts may indicate that PHP intensity is clinically needed before stepping down.
                  </Bullet>
                </ul>
                <p>
                  The best way to find out whether evening IOP is the right level of care is a free clinical assessment — which our admissions team can do by phone before you commit to anything.
                </p>

                {sectionRule}

                <h2 id="get-started" className="font-display font-bold mb-4 scroll-mt-24" style={h2Style}>
                  How to Get Started With Evening IOP in New Jersey
                </h2>
                <p className="mb-5">
                  Getting started begins with one call. Our admissions team will talk through your schedule, your clinical situation, and your insurance coverage — and confirm whether evening IOP is the right fit. If it is, most patients can begin within 24 to 48 hours of their initial assessment.
                </p>
                <p>
                  There is no obligation in calling. Most major insurance plans cover IOP — our admissions team{" "}
                  <Link to="/verify-insurance" className={linkCls} style={linkStyle}>
                    verify your insurance
                  </Link>{" "}
                  benefits before treatment begins so there are no surprises on cost. Evening or virtual scheduling. South Jersey and statewide. Free, confidential, available now.
                </p>

                {/* Bottom CTA callout */}
                <aside
                  className="mt-10 text-center"
                  style={{
                    background: CREAM,
                    borderTop: `4px solid ${BURGUNDY}`,
                    borderRadius: 12,
                    padding: 28,
                  }}
                >
                  <h3
                    className="font-display font-bold mb-3"
                    style={{ color: BURGUNDY, fontSize: 20 }}
                  >
                    Evening IOP Available Now at Cherry Hill Recovery Center, NJ
                  </h3>
                  <p className="mb-5" style={{ color: BODY, fontSize: 15, lineHeight: 1.7 }}>
                    Structured addiction treatment scheduled after work — same clinical quality as daytime IOP, flexible evening and virtual options. Serving South Jersey and all of New Jersey. Most insurance accepted.
                  </p>
                  <a
                    href={TEL}
                    aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
                    className="inline-flex items-center gap-2 font-semibold rounded-full"
                    style={{
                      background: BURGUNDY,
                      color: CREAM,
                      padding: "12px 28px",
                      marginBottom: 12,
                    }}
                  >
                    <Phone className="h-4 w-4" /> Call {PHONE}
                  </a>
                  <div>
                    <Link
                      to="/iop-cherry-hill-nj"
                      className="hover:underline"
                      style={{ color: BURGUNDY, fontWeight: 600 }}
                    >
                      Learn more about our IOP program →
                    </Link>
                  </div>
                </aside>

                {sectionRule}

                <InsuranceCTABlock />

                {/* FAQ */}
                <h2 id="faq" className="font-display font-bold mb-5 scroll-mt-24" style={h2Style}>
                  Frequently Asked Questions About Evening IOP in New Jersey
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((f, i) => (
                    <AccordionItem
                      key={f.q}
                      value={`item-${i}`}
                      className="border-b"
                      style={{ borderColor: RULE }}
                    >
                      <AccordionTrigger
                        className="text-left py-4 px-1 hover:no-underline"
                        style={{ color: BODY }}
                      >
                        <h3 className="font-semibold m-0" style={{ color: BODY, fontSize: 15 }}>
                          {f.q}
                        </h3>
                      </AccordionTrigger>
                      <AccordionContent
                        className="px-4 py-4 rounded-md"
                        style={{ background: CREAM, color: BODY, fontSize: 14, lineHeight: 1.7 }}
                      >
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Related posts */}
              <section className="mt-14">
                <h2 className="font-display font-bold mb-6" style={{ color: BURGUNDY, fontSize: 24 }}>
                  Related Articles
                </h2>
                <div className="grid md:grid-cols-3 gap-5">
                  {relatedPosts.map((p) => (
                    <Link
                      key={p.title}
                      to={p.href}
                      className="block rounded-xl p-5 transition-shadow hover:shadow-md"
                      style={{ background: "#FFFFFF", border: `1px solid ${RULE}` }}
                    >
                      <span
                        className="inline-block font-semibold mb-3"
                        style={{
                          background: CREAM,
                          color: BURGUNDY,
                          borderRadius: 50,
                          padding: "3px 12px",
                          fontSize: 11,
                        }}
                      >
                        IOP & PHP
                      </span>
                      <p
                        className="font-display font-semibold"
                        style={{ color: BURGUNDY, fontSize: 16, lineHeight: 1.35 }}
                      >
                        {p.title}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            </article>

            {/* Sticky sidebar */}
            <aside className="hidden lg:block">
              <div
                className="rounded-xl p-6 sticky"
                style={{ background: CREAM, borderTop: `4px solid ${BURGUNDY}`, top: 90 }}
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-white rounded-lg p-2">
                    <img src={logo} alt="Cherry Hill Recovery Center" width={158} height={40} className="h-10 w-auto" />
                  </div>
                </div>
                <p className="mb-4" style={{ color: "#555555", fontSize: 13, lineHeight: 1.6 }}>
                  Cherry Hill Recovery Center provides PHP, IOP, MAT, and dual-diagnosis addiction treatment in Cherry Hill, NJ — serving South Jersey and the greater Philadelphia region.
                </p>
                <a
                  href={TEL}
                  aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
                  className="flex items-center justify-center gap-2 w-full font-semibold py-3 rounded-full"
                  style={{ background: BURGUNDY, color: CREAM, marginBottom: 8 }}
                >
                  <Phone className="h-4 w-4" /> Call {PHONE}
                </a>
                <Link
                  to="/verify-insurance"
                  aria-label="Verify my insurance"
                  className="flex items-center justify-center w-full font-semibold py-3 rounded-full"
                  style={{ border: `2px solid ${BURGUNDY}`, color: BURGUNDY }}
                >
                  Verify My Insurance
                </Link>

                <hr className="my-5" style={{ borderColor: RULE }} />

                <p className="font-bold mb-3" style={{ color: BURGUNDY, fontSize: 13 }}>
                  In This Article
                </p>
                <ul className="space-y-2" style={{ fontSize: 13 }}>
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="hover:underline" style={{ color: BURGUNDY }}>
                        → {t.label}
                      </a>
                    </li>
                  ))}
                </ul>

                <hr className="my-5" style={{ borderColor: RULE }} />

                <p className="font-bold mb-2" style={{ color: BURGUNDY, fontSize: 13 }}>
                  Most Insurance Accepted
                </p>
                <p className="mb-3" style={{ color: "#555555", fontSize: 12, lineHeight: 1.6 }}>
                  Find out what your plan covers before you commit to anything.
                </p>
                <Link
                  to="/verify-insurance"
                  aria-label="Verify my insurance"
                  className="flex items-center justify-center w-full font-semibold py-2.5 rounded-full"
                  style={{ border: `2px solid ${BURGUNDY}`, color: BURGUNDY, fontSize: 14 }}
                >
                  Verify My Insurance
                </Link>
              </div>
            </aside>
          </div>
        </div>

        {/* End-of-page CTA banner */}
        <section
          className="py-14 px-6"
          style={{
            background: `linear-gradient(135deg, ${CRIMSON} 0%, ${BURGUNDY} 100%)`,
          }}
        >
          <div className="container max-w-4xl text-center">
            <h2
              className="font-display mb-4"
              style={{ color: "#FFFFFF", fontSize: "clamp(24px, 4vw, 32px)" }}
            >
              Ready to Start Evening IOP in New Jersey?
            </h2>
            <p className="mb-7" style={{ color: CREAM, fontSize: 18, lineHeight: 1.6 }}>
              Free confidential assessments available today. Most insurance accepted. Cherry Hill Recovery Center — {PHONE}.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={TEL}
                aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
                className="inline-flex items-center justify-center gap-2 font-semibold px-7 py-3 rounded-full"
                style={{ background: CREAM, color: BURGUNDY }}
              >
                <Phone className="h-4 w-4" /> Call {PHONE}
              </a>
              <Link
                to="/verify-insurance"
                aria-label="Verify my insurance"
                className="inline-flex items-center justify-center font-semibold px-7 py-3 rounded-full"
                style={{ border: `2px solid ${CREAM}`, color: CREAM }}
              >
                Verify My Insurance
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingCTAs />
    </div>
  );
};

export default BlogEveningIOPNJ;
